import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Clock, Database, Plus } from 'lucide-react';
import apiService from '../../services/apiService';
import composioService from '../../services/composioService';
import styles from './RightPanel.module.scss';

const nf0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const nfCompact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

function n0(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return nf0.format(n);
}

function nCompact(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return nfCompact.format(n);
}

function mapConnectionStatus(raw) {
  const s = String(raw || '').toLowerCase();
  if (['active', 'connected', 'success', 'completed'].includes(s)) return 'Connected';
  if (['pending', 'initiated', 'delayed', 'processing'].includes(s)) return 'Delayed';
  return 'Error';
}

function formatAgentDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Optional `integrations` from parent; otherwise loads Composio connections.
 */
export default function RightPanel({ integrations: integrationsProp }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState([]);
  const [totalTokens, setTotalTokens] = useState(0);
  const [stats, setStats] = useState({});
  const [integrations, setIntegrations] = useState([]);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const [statsRes, modelsRes, agentsRes, composioRes] = await Promise.all([
          apiService.getDashboardStats().catch(() => ({})),
          apiService.getAgentMetricsModels('24h', '').catch(() => ({})),
          apiService.getAgents(1, 12).catch(() => ({})),
          composioService.getConnectedApps().catch(() => ({})),
        ]);

        if (cancelled) return;

        const statsData = statsRes?.data || statsRes || {};
        setStats({
          failed_files: statsData.knowledge_base?.failed_files || 0,
          pending_files: statsData.knowledge_base?.pending_files || 0,
          failed_connected_apps: statsData.connected_apps?.failed || 0,
          pending_connected_apps: statsData.connected_apps?.pending || 0,
        });

        const root = modelsRes?.data ?? modelsRes;
        const list = Array.isArray(root)
          ? root
          : Array.isArray(root?.models)
            ? root.models
            : Array.isArray(root?.items)
              ? root.items
              : [];
        const sorted = [...list].sort((a, b) => Number(b.total_tokens || 0) - Number(a.total_tokens || 0));
        setModels(sorted.slice(0, 4));
        const sum = sorted.reduce((acc, m) => acc + Number(m.total_tokens || 0), 0);
        setTotalTokens(sum);

        const rawAgents = agentsRes?.agents || [];
        const withDates = [...rawAgents].sort((a, b) => {
          const ta = new Date(a.created_at || a.updated_at || 0).getTime();
          const tb = new Date(b.created_at || b.updated_at || 0).getTime();
          return tb - ta;
        });
        setAgents(withDates.slice(0, 5));

        const connRoot = composioRes?.connections || composioRes?.connected_apps || composioRes?.data?.connections;
        const conns = Array.isArray(connRoot) ? connRoot : [];
        setIntegrations(
          conns.map((c) => ({
            name: c.app_name || c.name || c.toolkit_name || c.toolkit_slug || 'Integration',
            status: mapConnectionStatus(c.status || c.connection_status),
          }))
        );
      } catch (e) {
        console.error('RightPanel load failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const integrationsList = integrationsProp?.length ? integrationsProp : integrations;

  const maxTokens = useMemo(() => Math.max(...models.map((m) => Number(m.total_tokens || 0)), 1), [models]);

  const attentionItems = useMemo(() => {
    const items = [];
    const ff = Number(stats.failed_files || 0);
    const fc = Number(stats.failed_connected_apps || 0);
    const pc = Number(stats.pending_connected_apps || 0);
    const pf = Number(stats.pending_files || 0);

    if (ff > 0) {
      items.push({
        id: 'kb-failed',
        title: 'Knowledge base',
        detail: `${n0(ff)} file(s) failed processing`,
        accent: 'coral',
      });
    }
    if (fc > 0) {
      items.push({
        id: 'int-failed',
        title: 'Integrations',
        detail: `${n0(fc)} connection(s) failed`,
        accent: 'coral',
      });
    }
    if (pc > 0 && items.length < 3) {
      items.push({
        id: 'int-pending',
        title: 'Integrations',
        detail: `${n0(pc)} connection(s) pending`,
        accent: 'amber',
      });
    }
    if (pf > 0 && items.length < 3) {
      items.push({
        id: 'kb-pending',
        title: 'Knowledge base',
        detail: `${n0(pf)} file(s) pending`,
        accent: 'amber',
      });
    }
    return items.slice(0, 3);
  }, [stats]);

  const accentClass = (accent) => {
    if (accent === 'amber') return { borderLeftColor: '#f59e0b' };
    return { borderLeftColor: 'var(--primary)' };
  };

  return (
    <aside className={`${styles.panel} scrollable-no-scrollbar`} aria-label="Dashboard side panel">
      <section>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>Model usage</h3>
          <span className={styles.liveBadge}>LIVE</span>
        </div>
        <div className={styles.card}>
          <p className={styles.subtle}>Token consumption by model</p>
          {loading ? (
            <p className={styles.emptyHint}>Loading…</p>
          ) : models.length === 0 ? (
            <p className={styles.emptyHint}>No model usage in the last 24h.</p>
          ) : (
            <>
              {models.map((m, idx) => {
                const model = m.model_id ?? m.model ?? `model-${idx}`;
                const provider = m.provider || '';
                const tokens = Number(m.total_tokens || 0);
                const calls = Number(m.calls || 0);
                const pct = Math.round((tokens / maxTokens) * 100);
                return (
                  <div key={`${model}-${idx}`} className={styles.modelRow}>
                    <div className={styles.modelTop}>
                      <div className={styles.modelNameRow}>
                        <div className={styles.modelIcon}>
                          <Database size={12} />
                        </div>
                        <span className={styles.modelName}>{model}</span>
                      </div>
                      {provider ? <span className={styles.modelProvider}>{provider}</span> : null}
                    </div>
                    <div className={styles.barTrack}>
                      <div className={styles.barFill} style={{ width: `${pct}%` }} />
                    </div>
                    <div className={styles.modelMeta}>
                      <span>Tokens: {nCompact(tokens)}</span>
                      <span>Calls: {n0(calls)}</span>
                    </div>
                  </div>
                );
              })}
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Total consumption</span>
                <span className={styles.totalValue}>{nCompact(totalTokens)} tokens</span>
              </div>
            </>
          )}
        </div>
      </section>

      <section>
        <h3 className={styles.sectionTitle} style={{ marginBottom: 12 }}>
          Systems needing attention
        </h3>
        <div className={styles.attentionList}>
          {attentionItems.length === 0 ? (
            <p className={styles.emptyHint}>No issues flagged from dashboard stats.</p>
          ) : (
            attentionItems.map((item) => (
              <div
                key={item.id}
                className={styles.attentionCard}
                style={accentClass(item.accent)}
              >
                <div className={styles.attentionInner}>
                  <AlertCircle
                    size={18}
                    style={{ color: item.accent === 'amber' ? '#f59e0b' : 'var(--primary)', flexShrink: 0 }}
                  />
                  <div>
                    <h4 className={styles.attentionTitle}>{item.title}</h4>
                    <p className={styles.attentionDesc}>{item.detail}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section>
        <h3 className={styles.sectionTitle} style={{ marginBottom: 12 }}>
          Integration health
        </h3>
        <div className={styles.integrationCard}>
          {integrationsList.length === 0 ? (
            <div style={{ padding: 14 }}>
              <p className={styles.emptyHint}>No integrations connected yet.</p>
              <button type="button" className={styles.linkBtn} onClick={() => navigate('/configure/tools')}>
                Add integrations
              </button>
            </div>
          ) : (
            integrationsList.map((item) => (
              <div key={item.name} className={styles.integrationRow}>
                <span className={styles.integrationName}>{item.name}</span>
                <div className={styles.integrationRight}>
                  <span className={styles.statusLabel}>{item.status}</span>
                  {item.status === 'Connected' ? (
                    <CheckCircle2 size={14} style={{ color: '#16a34a' }} />
                  ) : item.status === 'Delayed' ? (
                    <Clock size={14} style={{ color: '#f59e0b' }} />
                  ) : (
                    <AlertCircle size={14} style={{ color: 'var(--primary)' }} />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section>
        <div className={styles.recentHead}>
          <h3 className={styles.sectionTitle} style={{ margin: 0 }}>
            Recently created
          </h3>
          <button
            type="button"
            className={styles.addBtn}
            aria-label="Create agent"
            onClick={() => navigate('/agent-create')}
          >
            <Plus size={14} />
          </button>
        </div>
        <div className={styles.recentList}>
          {loading ? (
            <p className={styles.emptyHint}>Loading…</p>
          ) : agents.length === 0 ? (
            <p className={styles.emptyHint}>No agents yet.</p>
          ) : (
            agents.map((a) => {
              const label = formatAgentDate(a.created_at || a.updated_at || 0);
              return (
                <button
                  key={a.id}
                  type="button"
                  className={styles.recentRow}
                  onClick={() => navigate(`/agent-create?id=${a.id}`)}
                >
                  <div className={styles.recentText}>
                    <span className={styles.recentName}>{a.name || `Agent ${a.id}`}</span>
                    <span className={styles.recentDate}>{label ? `Created ${label}` : 'Recently updated'}</span>
                  </div>
                  <div className={styles.recentChev}>
                    <Plus size={14} />
                  </div>
                </button>
              );
            })
          )}
        </div>
      </section>
    </aside>
  );
}
